import {Panel} from './fields/panel/Panel.tsx';
import {TextField} from './fields/TextField.tsx';
import {DateTime} from './fields/DateTime.tsx';
import {Currency} from './fields/Currency.tsx';
import {Checkbox} from './fields/Checkbox.tsx';
import {Select} from './fields/Select.tsx';
import {DataGrid} from './fields/datagrid/DataGrid.tsx';
import {ConditionalWrapper} from './ConditionalWrapper';
import {TextArea} from "./fields/TextArea.tsx";
import {NumberField} from "./fields/NumberField.tsx";
import {Radio} from "./fields/Radio.tsx";
import {SimpleDate} from "./fields/SimpleDate.tsx";
import {LayoutGrid} from "./fields/LayoutGrid.tsx";
import {StaticField} from "./fields/staticfield/StaticField.tsx";
import {
    AerialComponent,
    APIComponent,
    BaseFormComponent,
    CheckboxComponent,
    CompSelectType,
    CompSummaryComponent,
    ConfigurableCompsSelectComponent,
    CurrencyComponent,
    CUSTOM_ERROR_TYPE,
    CustomErrorInfo,
    CustomValidatorComponent,
    DataGridComponent,
    DateTimeComponent,
    DerivedComponent,
    DuplicatedComponent,
    ExpandedImageUploadComponent,
    FileUploadComponent,
    FormComponent,
    FormSchema,
    FormType,
    FormValues,
    ImageUploadComponent,
    LayoutGridComponent,
    LicenseButtonsComponent,
    NumberComponent,
    PanelComponent,
    QCChecklistComponent,
    RadioComponent,
    SelectComponent,
    SimpleDateComponent,
    SpacerComponent,
    StaticFieldComponent,
    TextAreaComponent,
    TextFieldComponent,
    ValidationRuleLevel,
} from "./DynamicFormTypes.ts";
import {ApiComponentField} from "./fields/ApiComponentField.tsx";
import {FileUpload} from "./fields/FileUpload.tsx";
import {FieldErrors} from "react-hook-form";
import {ConfigurableCompsSelect} from "./fields/configurablecompsselect/ConfigurableCompsSelect.tsx";
import {ImageUpload} from "./fields/image/ImageUpload.tsx";
import {ExpandedImageUpload} from "./fields/image/ExpandedImageUpload.tsx";
import {CustomValidator} from "./fields/customvalidator/CustomValidator.tsx";
import {QCChecklist} from "./fields/qcchecklist/QCChecklist.tsx";
import {
    BasementType,
    ConstructionType,
    FormImageType,
    FormPermissions,
    FormSubmission,
    StandingType,
    StyleType,
    UserType
} from "../../api/ApiTypes.ts";
import {LicenseButtons} from "./fields/LicenseButtons.tsx";
import {Spacer} from "./fields/Spacer.tsx";
import {Duplicated} from "./fields/Duplicated.tsx";
import {CompSummary} from "./fields/CompSummary.tsx";
import {AerialMap} from "./fields/AerialMap.tsx";
import {DerivedRouter} from "./fields/derived/DerivedRouter.tsx";

// Define the interface for the function parameters
export interface RenderComponentProps {
    component: FormComponent;
    prefix?: string;
}

// Use the interface for the function parameter
export const renderComponent = ({component, prefix=''}: RenderComponentProps) => {
    // Get the type of the component to use for metadata lookup
    const type = component.type;

    const uniqueKey = prefix + component.componentId;

    if (!uniqueKey) {
        return (
            <div className="p-2 border border-red-300 bg-red-50 rounded">
                <p className="text-sm">Component does not have a unique key</p>
            </div>
        );
    }
    // Type-safe component rendering using a switch statement
    const renderSafeComponent = () => {
        // Create props object WITHOUT the key property
        switch (type) {
            case FormType.PANEL:
                return <Panel key={uniqueKey} component={component as PanelComponent} />;
            case FormType.TEXTFIELD:
                return <TextField key={uniqueKey} component={component as TextFieldComponent} />;
            case FormType.TEXTAREA:
                return <TextArea key={uniqueKey} component={component as TextAreaComponent}  />;
            case FormType.NUMBER:
                return <NumberField key={uniqueKey} component={component as NumberComponent} />;
            case FormType.DATETIME:
                return <DateTime key={uniqueKey} component={component as DateTimeComponent} />;
            case FormType.CURRENCY:
                return <Currency key={uniqueKey} component={component as CurrencyComponent} />;
            case FormType.CHECKBOX:
                return <Checkbox key={uniqueKey} component={component as CheckboxComponent} />;
            case FormType.RADIO:
                return <Radio key={uniqueKey} component={component as RadioComponent} />;
            case FormType.SELECT:
                return <Select key={uniqueKey} component={component as SelectComponent} />;
            case FormType.DATAGRID:
                return <DataGrid key={uniqueKey} component={component as DataGridComponent} />;
            case FormType.SIMPLE_DATE:
                return <SimpleDate key={uniqueKey} component={component as SimpleDateComponent} />;
            case FormType.LAYOUT_GRID:
                return <LayoutGrid key={uniqueKey} component={component as LayoutGridComponent} />;
            case FormType.STATIC_FIELD:
                return <StaticField key={uniqueKey} component={component as StaticFieldComponent} />
            case FormType.API_COMPONENT:
                return <ApiComponentField key={uniqueKey} component={component as APIComponent} />
            case FormType.FILE_UPLOAD:
                return <FileUpload key={uniqueKey} component={component as FileUploadComponent} />
            case FormType.CONFIGURABLE_COMPS_SELECT:
                return <ConfigurableCompsSelect key={uniqueKey} component={component as ConfigurableCompsSelectComponent} />
            case FormType.DERIVED:
                return <DerivedRouter key={uniqueKey} component={component as DerivedComponent} />
            case FormType.IMAGE_UPLOAD:
                return <ImageUpload key={uniqueKey} component={component as ImageUploadComponent}/>
            case FormType.EXPANDED_IMAGE_UPLOAD:
                return <ExpandedImageUpload key={uniqueKey} component={component as ExpandedImageUploadComponent}/>
            case FormType.CUSTOM_VALIDATOR:
                return <CustomValidator key={uniqueKey} component={component as CustomValidatorComponent}/>
            case FormType.LICENSE_BUTTONS:
                return <LicenseButtons key={uniqueKey} component={component as LicenseButtonsComponent} />
            case FormType.SPACER:
                return <Spacer key={uniqueKey} component={component as SpacerComponent} />
            case FormType.AERIAL_MAP:
                return <AerialMap key={uniqueKey} component={component as AerialComponent} />
            case FormType.DUPLICATED:
                return <Duplicated key={uniqueKey} component={component as DuplicatedComponent} />
            case FormType.COMP_SUMMARY:
                return <CompSummary key={uniqueKey} component={component as CompSummaryComponent} />
            case FormType.QC_CHECKLIST:
                return <QCChecklist key={uniqueKey} component={component as QCChecklistComponent} />
            default:
                return (
                    <div key={uniqueKey} className="p-2 border border-red-300 bg-red-50 rounded">
                        <p className="text-sm">Failed to render component: Type not handled in switch</p>
                    </div>
                );
        }
    };

    // If component has conditional logic, wrap it in ConditionalWrapper
    if (component.conditional) {
        return (
            <ConditionalWrapper
                key={`${uniqueKey}-conditional`}
                component={component}
            >
                {renderSafeComponent()}
            </ConditionalWrapper>
        );
    }

    // If no conditional logic, render component directly
    return renderSafeComponent();
};


export function flattenComponents(components: FormComponent[]): Map<string, FormComponent> {
    const flattenedMap = new Map<string, FormComponent>();

    const flattenComponents = (components: FormComponent[]) => {
        components.forEach((component) => {
            flattenedMap.set(component.fieldKey, component);
            if ('components' in component && Array.isArray(component.components)) {
                // Recursively flatten nested components
                flattenComponents(component.components);
            }
            if (component.type === FormType.DATAGRID &&
                "headerConfig" in component &&
                 component.headerConfig &&
                "components" in component.headerConfig &&
                Array.isArray(component.headerConfig.components)
            ) {
                // Recursively flatten datagrid components
                flattenComponents(component.headerConfig.components);
            }
        });
    };

    flattenComponents(components || []);
    return flattenedMap;
}

export function flattenSchema(schema: FormSchema): Map<string, FormComponent> {
    return flattenComponents(schema.components || []);
}

/**
 * Types that are layout-only containers or non-value elements.
 * These don't hold form values and should be skipped when collecting descendant fields.
 */
const IGNORE_WARNING: Set<FormType> = new Set([
    FormType.LAYOUT_GRID,
    FormType.PANEL,
    FormType.SPACER,
    FormType.STATIC_FIELD,
]);

/**
 * Recursively collects all descendant fields (with labels) from a component tree.
 * Skips layout-only types and collects leaf fields that hold actual form values.
 * For DataGrid components, collects the datagrid fieldKey itself (not individual row fields).
 */
export function collectDescendantFields(
    component: BaseFormComponent
): { fieldKey: string; label: string }[] {
    const results: { fieldKey: string; label: string }[] = [];

    const walk = (comp: BaseFormComponent) => {

        // DataGrid: collect the grid itself as a single field (its value is the full array)
        if (comp.type === FormType.DATAGRID) {
            results.push({
                fieldKey: comp.fieldKey,
                label: comp.label || comp.fieldKey,
            });
            return;
        }

        // If this component has nested children, recurse (e.g., a panel with showLabel)
        if ('components' in comp && Array.isArray(comp.components) && comp.components.length > 0) {
            (comp.components as BaseFormComponent[]).forEach(walk);
            return;
        }

        if (IGNORE_WARNING.has(comp.type)) {
            return;
        }
        // Leaf field - collect it
        results.push({
            fieldKey: comp.fieldKey,
            label: comp.label || comp.fieldKey,
        });
    };
    //we will include ourself now
    walk(component)
    return results;
}

export const isCompTypeActive = (compType: CompSelectType | undefined): boolean => {
    return compType === CompSelectType.ACTIVE;
}

export const compareMlsIdsOrAddressesMatch = (address1: string | undefined, address2: string | undefined, mlsId1: string | undefined, mlsId2: string | undefined): boolean => {
    if (mlsId1 !== undefined && mlsId2 !== undefined) {
        return mlsId1 === mlsId2;
    }

    if (address1 === undefined || address2 === undefined) {
        return false;
    }

    // standardize abbreviations, punctuation and whitespace
    function normalizeAddress(address: string): string {
        let normalized = address.toLowerCase().trim();

        // remove punctuation
        normalized = normalized.replace(/[.,;:!?#]/g, '');

        const abbreviations: Record<string, string> = {
            'road': 'rd',
            'street': 'st',
            'avenue': 'ave',
            'boulevard': 'blvd',
            'drive': 'dr',
            'lane': 'ln',
            'court': 'ct',
            'circle': 'cir',
            'place': 'pl',
            'parkway': 'pkwy',
            'highway': 'hwy',
            'apartment': 'apt',
            'suite': 'ste',
            'north': 'n',
            'south': 's',
            'east': 'e',
            'west': 'w',
            'northeast': 'ne',
            'northwest': 'nw',
            'southeast': 'se',
            'southwest': 'sw'
        };

        Object.entries(abbreviations).forEach(([key, value]) => {
            // replacing long form words with their abbreviation
            normalized = normalized.replace(new RegExp(`\\b${key}\\b`, 'g'), value);
        });

        // removing whitespace
        normalized = normalized.replace(/\s+/g, ' ').trim();

        return normalized;
    }

    // separate street number from rest of address
    const separateStreetNumber = (addr: string): { streetNumber: string | null, restOfAddress: string } => {
        // separates street number (with an optional single letter) - bounded by a space - from the rest of the address, which could still contain numbers
        const match = addr.match(/^\s*(\d+[a-zA-Z]?)\b\s*(.*)/);
        if (match) {
            return {
                streetNumber: match[1],
                restOfAddress: normalizeAddress(match[2])
            };
        }
        return {
            streetNumber: null,
            restOfAddress: normalizeAddress(addr)
        };
    }

    const { streetNumber: streetNumber1, restOfAddress: restOfAddress1 } = separateStreetNumber(address1);
    const { streetNumber: streetNumber2, restOfAddress: restOfAddress2 } = separateStreetNumber(address2);

    // street number must match exactly
    if (!streetNumber1 || !streetNumber2 || streetNumber1 !== streetNumber2) {
        return false;
    }

    if (restOfAddress1 === restOfAddress2) {
        return true;
    }

    // calculates similarity
    const threshold = 0.9;
    const levenshteinDistance = (str1: string, str2: string): number => {
        const matrix: number[][] = [];

        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }

        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }

        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1, // substitution
                        matrix[i][j - 1] + 1,     // insertion
                        matrix[i - 1][j] + 1      // deletion
                    );
                }
            }
        }

        return matrix[str2.length][str1.length];
    };

    const distance = levenshteinDistance(restOfAddress1, restOfAddress2);
    const maxLength = Math.max(restOfAddress1.length, restOfAddress2.length);

    if (maxLength === 0) return true;

    // rest of address must match on 90% of characters or better
    const similarity = 1 - (distance / maxLength);
    return similarity >= threshold;
}

export function getConstructionTypeDisplay(type: ConstructionType | undefined): string {
    if (!type) {
        return "Unknown";
    }
    switch (type) {
        case ConstructionType.MODULAR:
            return "Modular";
        case ConstructionType.SITE_BUILT:
            return "Site-Built";
        case ConstructionType.MANUFACTURED:
            return "Manufactured";
        default:
            return "Unknown";
    }
}

export function getBasementTypeDisplay(type: BasementType | undefined): string {
    if (!type) {
        return "Unknown";
    }
    switch (type) {
        case BasementType.BASEMENT:
            return "Basement";
        case BasementType.PARTIAL_BASEMENT:
            return "Partial Basement";
        case BasementType.CRAWLSPACE:
            return "Crawlspace";
        case BasementType.SLAB:
            return "Slab";
        case BasementType.OTHER:
            return "Other";
        case BasementType.NONE:
            return "None";
        default:
            return "Unknown";
    }
}


export function getStandingTypeDisplay(type: StandingType | undefined): string {
    if (!type) {
        return "Unknown";
    }
    switch (type) {
        case StandingType.DETACHED:
            return "Detached";
        case StandingType.ATTACHED:
            return "Attached";
        default:
            return "Unknown";
    }
}

export function getStyleTypeDisplay(type: StyleType | undefined): string {
    if (!type) {
        return "Unknown";
    }
    switch (type) {
        case StyleType.BI_LEVEL:
            return "Bi-Level";
        case StyleType.BUNGALOW:
            return "Bungalow";
        case StyleType.CAPE_COD:
            return "Cape Cod";
        case StyleType.COLONIAL:
            return "Colonial";
        case StyleType.CONTEMPORARY:
            return "Contemporary";
        case StyleType.COTTAGE:
            return "Cottage";
        case StyleType.FARM_HOUSE:
            return "Farm House";
        case StyleType.MOBILE_HOME_DOUBLE_WIDE:
            return "Mobile Home- Double Wide";
        case StyleType.MOBILE_HOME_SINGLE_WIDE:
            return "Mobile Home- Single Wide";
        case StyleType.TWO_FAMILY:
            return "2 Family";
        case StyleType.RANCH:
            return "Ranch";
        case StyleType.TRI_LEVEL:
            return "Tri-Level";
        case StyleType.VACANT_LOT:
            return "Vacant Lot";
        case StyleType.VICTORIAN:
            return "Victorian";
        case StyleType.OTHER_SINGLE_FAMILY:
            return "Other Single Family";
        case StyleType.SPLIT:
            return "Split";
        case StyleType.THREE_FAMILY:
            return "3 Family";
        case StyleType.FOUR_FAMILY:
            return "4 Family";
        case StyleType.GARDEN_CONDO:
            return "Garden Condo";
        case StyleType.TOWNHOUSE_CONDO:
            return "Townhouse Condo";
        case StyleType.HIGHRISE_CONDO:
            return "Highrise Condo";
        default:
            return "Unknown";
    }
}

export function getUnitsDisplay(input: number | undefined): string {
    switch (input) {
        case 1:
        case 2:
        case 3:
        case 4:
            return input.toString();
        case 5:
            return "5+";
        default:
            return "";
    }
}

export function getDatasourceDisplay(input: string | undefined): string {
    let label: String;
    let value: String;

    switch (input) {
        case "DATA_QUICK":
            label = "Dataquick";
            value = "dataquick";
            break;
        case "MLS_10":
        case "MLS_10_DQ":
            label = "Multiple Listing Site";
            value = "mls";
            break;
        default:
            label = "Unknown";
            value = "unknown";
            break;
    }

    const jsonNode = {
        label: label,
        value: value
    }
    return JSON.stringify(jsonNode);
}

export function getImageTypeDisplay(input: FormImageType): string {
    switch (input) {
        case FormImageType.NONE:
            return 'NONE';
        case FormImageType.FRONT:
            return 'Subject Photo of Front';
        case FormImageType.STREET:
            return 'Subject Street Scene';
        case FormImageType.OTHER:
            return 'Subject Street Scene Facing Other Direction';
        case FormImageType.SIDE:
            return 'Subject Photo of Side';
        case FormImageType.BACK:
            return 'Subject Photo of Back';
        case FormImageType.FROM_BACK:
            return 'Subject View From Back of House';
        case FormImageType.FROM_FRONT:
            return 'Subject View From Front of House';
        case FormImageType.ADDRESS:
            return 'Subject Address Verification Photo';
        case FormImageType.BED:
            return 'Bedroom';
        case FormImageType.BATH:
            return 'Bathroom';
        case FormImageType.KITCHEN:
            return 'Kitchen';
        case FormImageType.BASEMENT:
            return 'Basement';
        case FormImageType.ATTIC:
            return 'Attic';
        case FormImageType.EXTERIOR:
            return 'Additional Exterior Subject Photo';
        case FormImageType.UNIT:
            return 'Unit Number';
        case FormImageType.LEFT:
            return 'Subject Left Side';
        case FormImageType.RIGHT:
            return 'Subject Right Side';
        case FormImageType.ACROSS:
            return 'Photo of House Across the Street';
        case FormImageType.AERIAL:
            return 'Aerial View';
        case FormImageType.BACKYARD:
            return 'Subject Backyard';
        case FormImageType.SIGN:
            return 'Subject Street Sign';
        case FormImageType.FLOORPLAN:
            return 'Floor Plan Sketch';
        case FormImageType.LIVING:
            return 'Main Living Area';
        case FormImageType.DINING:
            return 'Dining Room';
        case FormImageType.INTERIOR:
            return 'Additional Interior Subject Photo';
        case FormImageType.COMPARABLE:
            return 'Comparable Photo';
        case FormImageType.BPO_DISCLOSURE:
            return 'BPO Disclosure Document';
    }
}

export function getGarageDisplay(input: string | undefined): string {
    let lowercase: string;

    switch (input) {
        case "ATTACHED":
            lowercase = "Attached";
            break;
        case "DETACHED":
            lowercase = "Detached";
            break;
        case "CARPORT":
            lowercase = "Carport";
            break;
        case "OPEN_ASSIGNED":
            lowercase = "Open Assigned";
            break;
        case "COVERED_ASSIGNED":
            lowercase = "Covered Assigned";
            break;
        case "BUILT_IN":
            lowercase = "Built In";
            break;
        case "NONE":
            lowercase = "None";
            break;
        case "OTHER":
            lowercase = "Other";
            break;
        default:
            lowercase = "Unknown";
            break;
    }

    return valueToJsonSelectObject(lowercase);
}

export function valueToJsonSelectObject(value: string): string {
    const jsonNode = {
        label: value,
        value: value
    }

    return JSON.stringify(jsonNode);
}


export const defaultParserForSelects = (value: any) => {
    if (typeof value === 'string' && value.includes('{')) {
        try {
            return JSON.parse(value);
        } catch (e) {
            console.error("Error parsing JSON string:", e);
        }
    }
    return value;
}

export interface ErrorFeedback {
    warnings: ErrorMessage[],
    errors: ErrorMessage[],
    nonNestedWarnings: ErrorMessage[],
    nonNestedErrors: ErrorMessage[],
    totalErrorsCount: number,
}

export interface ErrorMessage {
    label: string;
    message?: string;
    fieldKey: string;
    nested?: ErrorMessage[];
    level: ValidationRuleLevel;
}

export interface DataGridKey {
    fieldKey: string;
    dataGrid?: string;
    row?: number;
}

//use this to extract fieldkeys ex: datagrid.0.address would return { dataGrid: "datagrid", row: 0, fieldKey: "address"}. If the key does not follow the datagrid format and is just a simple fieldkey, it will return { fieldKey: "theOriginalKey"}
export const extractDataGridKey = (fullFieldKey: string): DataGridKey => {
    const [dataGrid, row, fieldKey] = fullFieldKey.split(".");
    if (fieldKey) {
        return {
            fieldKey: fieldKey,
            dataGrid: dataGrid,
            row: Number(row),
        }
    }
    return {
        fieldKey: fullFieldKey
    };
}

export const toTitleCase = (str: string): string => {
    try {
        if (!str) return str;
        // Convert to lowercase and then capitalize the first letter of each word
        return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    }
    catch (e) {
        return str;
    }

};

// Convert a string to proper case, handling common street suffixes and directionals
export const streetProperCase = (str: string): string => {
    try {
        if (!str) return str;

        // Common street suffixes and directionals that should be capitalized
        const exceptions = new Set([
            'st', 'nd', 'rd', 'th', 'street', 'ave', 'avenue', 'blvd', 'boulevard',
            'rd', 'road', 'ln', 'lane', 'dr', 'drive', 'ct', 'court', 'pl', 'place',
            'way', 'circle', 'cir', 'pkwy', 'parkway', 'n', 'north', 's', 'south',
            'e', 'east', 'w', 'west', 'ne', 'nw', 'se', 'sw', 'apt', 'apartment',
            'unit', 'suite', 'ste', 'floor', 'fl'
        ]);

        return str.toLowerCase().replace(/\b\w+/g, (word) => {
            const lowerWord = word.toLowerCase();
            if (exceptions.has(lowerWord)) {
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        });
    }
    catch (e) {
        console.error("Error in toProperCase:", e);
        return str;
    }

};

const FIELD_LABEL_OVERRIDES = new Map([
    ["asIsListPrice90", "As Is - List Price 90 Days"],
    ["asIsSalePrice90", "As Is - Sale Price 90 Days"],
    ["asRepairedListPrice90", "As Repaired - List Price 90 Days"],
    ["asRepairedSalePrice90", "As Repaired - Sale Price 90 Days"],
    ["asIsListPrice30", "As Is - List Price 30 Days"],
    ["asIsSalePrice30", "As Is - Sale Price 30 Days"],
    ["asRepairedListPrice30", "As Repaired - List Price 30 Days"],
    ["asRepairedSalePrice30", "As Repaired - Sale Price 30 Days"],
]);

const getOverrideLabel = (fieldKey : string): string | undefined => {
    return FIELD_LABEL_OVERRIDES.get(fieldKey);
}

// Process field key to get nested path info
export const getLabel = (fieldKey: string, componentMap: Map<string, FormComponent>): string  => {
    let mainKey = extractDataGridKey(fieldKey);
    let mainLabel = getOverrideLabel(mainKey.fieldKey) || componentMap?.get(mainKey.fieldKey)?.label || mainKey.fieldKey;
    if (!mainKey.dataGrid) {
        return mainLabel;
    }
    let dataGridLabel = getOverrideLabel(mainKey.fieldKey) || componentMap?.get(mainKey.dataGrid)?.label || mainKey.dataGrid;
    return `${dataGridLabel} # ${(mainKey.row ?? 0) + 1} -> ${mainLabel}`;
};

export const setRuleType = (level?: ValidationRuleLevel, type?: CUSTOM_ERROR_TYPE): string => {
    return `${level || ValidationRuleLevel.ERROR}_${type || CUSTOM_ERROR_TYPE.FACTORY}`;
}

export const parseRuleType = (errorTypeRaw: string | undefined): CustomErrorInfo => {
    if (!errorTypeRaw) {
        return {
            level: ValidationRuleLevel.ERROR,
            type: CUSTOM_ERROR_TYPE.FACTORY,
        };
    }

    const level = errorTypeRaw.startsWith(ValidationRuleLevel.WARNING) ? ValidationRuleLevel.WARNING : ValidationRuleLevel.ERROR;
    let type = CUSTOM_ERROR_TYPE.FACTORY;
    //rules should always start with a level, we will default to error if none is provided.

    if (errorTypeRaw.includes(CUSTOM_ERROR_TYPE.FACTORY)) {
        type = CUSTOM_ERROR_TYPE.FACTORY;
    }
    else if (errorTypeRaw.includes(CUSTOM_ERROR_TYPE.MANUAL)) {
        type = CUSTOM_ERROR_TYPE.MANUAL;
    }
    else if (errorTypeRaw.includes(CUSTOM_ERROR_TYPE.BACKEND)) {
        type = CUSTOM_ERROR_TYPE.BACKEND;
    }
    return {
        level,
        type
    };
}

// Recursively build the error message tree
export const buildErrorMessages = (errorObj: FieldErrors<FormValues>, componentMap: Map<string, FormComponent>, path = ''): ErrorMessage[] => {
    return Object.entries(errorObj)
        .map(([key, value]) => {
            const errorTypeString = value?.type ? value?.type.toString() : undefined;
            const errorInfo = parseRuleType(errorTypeString)
            const { level, type } = errorInfo;
            if (type.includes(CUSTOM_ERROR_TYPE.BACKEND)) {
                console.info("Skipping backend error for key:", key, "with value:", value);
                //skip backend errors
                return null;
            }
            //the path is for the nested strucutre w'ell feed in "parentComponent.0" [parentFieldkey.index]
            const currentPath = path ? `${path}.${key}` : key;
            const label = getLabel(currentPath, componentMap);

            // Handle array errors
            if (Array.isArray(value)) {
                // Create nested errors for array items
                const nestedErrors: ErrorMessage[] = [];
                let validErrorCount = 0;

                value.forEach((item, index) => {
                    if (item && typeof item === 'object') {
                        validErrorCount ++;
                        const itemErrors = buildErrorMessages(item as any, componentMap, `${currentPath}.${index}`);
                        nestedErrors.push(...itemErrors);
                    }
                });

                const hasOneRealError = nestedErrors.some(err => err.level && err.level.startsWith(ValidationRuleLevel.ERROR));
                return {
                    fieldKey: currentPath,
                    label,
                    nested: nestedErrors.length > 0 ? nestedErrors : undefined,
                    level: hasOneRealError ? ValidationRuleLevel.ERROR : ValidationRuleLevel.WARNING
                };
            }

            // Handle object errors with nested properties
            if (value && typeof value === 'object') {
                return {
                    fieldKey: currentPath,
                    label,
                    message: value.message ? value.message : `Validation error for ${label}`,
                    level: level
                };
            }

            return null;
        })
        .filter(Boolean) as ErrorMessage[];
};

export interface ErrorCount {
    errors: number;
    warnings: number;
    total: number;
}

export const getErrorCount =  (errorObj: FieldErrors<FormValues>): ErrorCount => {
    let errorCount = 0;
    let warningCount = 0;
    for (const key in errorObj) {
        const value = errorObj[key];
        if (Array.isArray(value)) {
            let isWarningForArray = true;
            for (const item of value) {
                const counts = getErrorCount(item as any);
                if (counts.errors > 0) {
                    isWarningForArray = false;
                }
                errorCount += counts.errors;
                warningCount += counts.warnings;
            }
            //set an error for the entire array as well (datagrid level error)
            if (isWarningForArray) {
                warningCount++;
            }
            else {
                errorCount++;
            }
        }
        else {
            const errorTypeString = value?.type ? value?.type.toString() : undefined;
            const errorInfo = parseRuleType(errorTypeString)
            if (errorInfo.type === CUSTOM_ERROR_TYPE.BACKEND) {
                continue;
            }
            if (errorInfo.level === ValidationRuleLevel.WARNING) {
                warningCount++;
            }
            else {
                errorCount++;
            }
        }
    }
    return { errors: errorCount, warnings: warningCount , total: errorCount + warningCount };
}

// Sort errors before warnings
const sortErrorTree = (list: ErrorMessage[]) => {
    const isWarning = (e: ErrorMessage) =>
        (e.level ?? ValidationRuleLevel.ERROR) === ValidationRuleLevel.WARNING;

    list.sort((a, b) => Number(isWarning(a)) - Number(isWarning(b)));

    list.forEach(e => e.nested?.length && sortErrorTree(e.nested));
};

export const getErrorFeedback = (errorObj: FieldErrors<FormValues>, componentMap: Map<string, FormComponent>): ErrorFeedback => {
    const allErrors = buildErrorMessages(errorObj, componentMap);

    sortErrorTree(allErrors);

    // Separate warnings and errors
    const warnings: ErrorMessage[] = [];
    const errors: ErrorMessage[] = [];
    const nonNestedWarnings: ErrorMessage[] = [];
    const nonNestedErrors: ErrorMessage[] = [];
    let totalErrorsCount = 0;

    const categorizeErrorMessage = (errorMessage: ErrorMessage, isTopLevel: boolean = true): void => {
        // Add to hierarchical arrays only if this is a top-level message
        totalErrorsCount ++;
        if (isTopLevel) {
            if (errorMessage.level === ValidationRuleLevel.WARNING) {
                warnings.push(errorMessage);
            } else {
                errors.push(errorMessage);
            }
        }

        // Always add to flattened arrays
        if (errorMessage.level === ValidationRuleLevel.WARNING) {
            nonNestedWarnings.push(errorMessage);
        } else {
            nonNestedErrors.push(errorMessage);
        }

        // Recursively process nested errors
        if (errorMessage.nested && errorMessage.nested.length > 0) {
            errorMessage.nested.forEach(nestedError => {
                categorizeErrorMessage(nestedError, false); // Not top-level
            });
        }
    };

// Process all error messages
    allErrors.forEach(error => categorizeErrorMessage(error, true));

    return {
        warnings,           // Top-level warnings (maintains hierarchy)
        errors,             // Top-level errors (maintains hierarchy)
        nonNestedWarnings,  // All warnings flattened
        nonNestedErrors,     // All errors flattened
        totalErrorsCount
    };
}

export const scrollToError = (error: ErrorMessage) => {
    try {
        let element = document.getElementById(error.fieldKey);

        // If element is hidden (file input), find its label instead
        if (element && window.getComputedStyle(element).display === 'none') {
            // First try: label with for attribute
            let label: HTMLElement | null = document.querySelector(`label[for="${error.fieldKey}"]`);

            // Second try: parent label (wrapping pattern)
            if (!label) {
                label = element.closest('label');
            }

            // Third try: adjacent sibling label
            if (!label) {
                const prev = element.previousElementSibling;
                const next = element.nextElementSibling;
                label = (prev?.tagName === 'LABEL' ? prev as HTMLElement : null) ||
                    (next?.tagName === 'LABEL' ? next as HTMLElement : null);
            }

            element = label || element; // fallback to original element if no label found
        } else if (!element) {
            // Try label if element doesn't exist
            element = document.querySelector(`label[for="${error.fieldKey}"]`);
        }

        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();

            const level = error.level || ValidationRuleLevel.ERROR;

            // Define classes based on level
            let ringColor: string;
            let bgColor: string;

            switch (level) {
                case ValidationRuleLevel.WARNING:
                    ringColor = 'ring-yellow-500';
                    bgColor = 'bg-yellow-50';
                    break;
                case ValidationRuleLevel.ERROR:
                default:
                    ringColor = 'ring-red-500';
                    bgColor = 'bg-red-50';
                    break;
            }

            // Add classes based on error level
            element.classList.add(
                'ring-2',
                ringColor,
                'ring-offset-2',
                bgColor,
                'transition-all',
                'duration-300'
            );

            setTimeout(() => {
                try {
                    element.classList.remove(
                        'ring-2',
                        ringColor,
                        'ring-offset-2',
                        bgColor,
                        'transition-all',
                        'duration-300'
                    );
                } catch (e) {
                    // Ignore if element no longer exists
                }
            }, 2000);
        }
    } catch (e) {
        console.warn('Error scrolling to field:', e);
    }
};

export const scrollToSection = (fieldKey: string) => {
    try {
        let element = document.getElementById(fieldKey);
        if (!element) {
            element = document.querySelector(`label[for="${fieldKey}"]`);
        }
        if (element) {
            const elementTop = element.getBoundingClientRect().top + window.scrollY;
            const targetScroll = elementTop - (130);
            window.scrollTo({ top: targetScroll, behavior: 'smooth' });
        }
    } catch (e) {
        console.warn('Error scrolling to field:', e);
    }
};

export const extractSchemaFieldKey = (fieldKey: string) => {
    if (!fieldKey.includes(".")) {
        return fieldKey;
    }
    return fieldKey.split(".")[2];
}

export enum QC_RESOLUTION {
    APPROVE = "APPROVE",
    REJECT = "REJECT",
    CANCEL = "CANCEL",
}

export enum SUBMIT_MODE {
    READ_ONLY = "READ_ONLY",
    ADMIN = "ADMIN",
    QC = "QC",
    APPRAISER_SUBMIT = "APPRAISER_SUBMIT",
}
export enum FormSubmissionStatus {
    IN_PROGRESS=  "IN_PROGRESS",
    SUBMITTED = "SUBMITTED"
}

export const getFormPermissions = (mode: SUBMIT_MODE, _submission?: FormSubmission, user?: UserType ): FormPermissions => {
    const userType = user || UserType.REGISTERED;
    /**
    * history does not matter, if isForAdmin (then they are on the form eefinition edit screen, we allow general behavior to see good representatioon of demo form)
    * */
    switch (mode) {
        // use this mode for testing out forms
        case SUBMIT_MODE.ADMIN: {
            return {
                submitMode: mode,
                userType: userType,
                canSave: false,
                canEdit: true,
                allowTriggerApiComponent: true
            }
        }
        //this mode is for qcing of the order
        case SUBMIT_MODE.QC: {
            return {
                submitMode: mode,
                userType: userType,
                canSave: false,
                canEdit: true,
                allowTriggerApiComponent: false
            }
        }
        //this mode is for normal appraiser submit
        case SUBMIT_MODE.APPRAISER_SUBMIT: {
            return {
                submitMode: mode,
                userType: userType,
                canSave: true,
                canEdit: true,
                allowTriggerApiComponent: true
            }
        }
        default: {
            return {
                submitMode: SUBMIT_MODE.READ_ONLY,
                userType: userType,
                canSave: false,
                canEdit: false,
                allowTriggerApiComponent: false
            }
        }
    }

}

